import { createFileRoute } from "@tanstack/react-router";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import {
  UserAndCoachPersonalitySchema,
  coachPersonalityField,
  personalityStep,
  userPersonalityField,
} from "~/lib/schemas/coach-discovery";
import type { UserAndCoachPersonalityForm } from "~/lib/schemas/coach-discovery";
import { createNoIndexMeta } from "~/lib/shared/meta";

export const Route = createFileRoute("/_public/discovery/steps/6/")({
  head: () =>
    createNoIndexMeta(
      "Step 6: Personality Preferences – Dream Coach Match",
      "Define your and your coach's personality preferences in our discovery process",
    ),
  component: DiscoveryStep6,
});

function DiscoveryStep6() {
  const { form, handleNextStep, handlePreviousStep } = useDiscoveryForm({
    step: 6,
    schema: UserAndCoachPersonalitySchema,
  });
  const { handleSubmit, formState, control, watch } = form;
  const { errors, isSubmitting, isValid } = formState;

  const watchedUserPersonality = watch("userPersonality") || [];
  const watchedCoachPersonality = watch("coachPersonality") || [];

  const tooManyUserPersonality =
    !watchedUserPersonality.includes("Skip this question") &&
    watchedUserPersonality.length > 2;
  const tooManyCoachPersonality =
    !watchedCoachPersonality.includes("Skip this question") &&
    watchedCoachPersonality.length > 2;

  const onSubmit = (_data: UserAndCoachPersonalityForm) => {
    handleNextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title={personalityStep.title} />
      <DiscoveryStep.SubTitle title={personalityStep.subTitle} />

      <DiscoveryStep.Card className="mt-6">
        <FormField
          labelSize="xl"
          error={errors.userPersonality?.message}
          label={userPersonalityField.subTitle}
        >
          <CheckboxGroupField
            name="userPersonality"
            control={control}
            options={userPersonalityField.options || []}
            exclusiveOptions={["Skip this question"]}
          />
          {tooManyUserPersonality && (
            <p className="mt-2 text-sm text-red-600">
              You cannot select more than 2 options. Please uncheck one to
              continue.
            </p>
          )}
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Card className="mt-6">
        <FormField
          labelSize="xl"
          error={errors.coachPersonality?.message}
          label={coachPersonalityField.subTitle}
        >
          <CheckboxGroupField
            name="coachPersonality"
            control={control}
            options={coachPersonalityField.options || []}
            exclusiveOptions={["Skip this question"]}
          />
          {tooManyCoachPersonality && (
            <p className="mt-2 text-sm text-red-600">
              You cannot select more than 2 options. Please uncheck one to
              continue.
            </p>
          )}
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        prev={handlePreviousStep}
        type="submit"
        loading={isSubmitting}
        disabled={!isValid}
      />
    </form>
  );
}
